import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {FeatureTypeService, NotificationsService} from '@catcore/services';
import {Feature} from '@catcore/services/feature/feature';
import {GenericLinkedResource} from '@catcore/types/base';
import {GenericResourceApiService} from '@catfeatures/services/generic-resource-api.service';
import {navigateToFeatureResource} from '@catfeatures/shared/utils';
import {FeatureGgwImplementationsService} from '@catmodules/models/feature-ggw-implementations/services/feature-ggw-implementations.service';
import {FeatureUnitsService} from '@catmodules/models/feature-units/services/feature-units.service';
import {FeatureUnit} from '@catmodules/models/feature-units/types';
import {DisplayTypes} from '@catui/components/panel/types';
import {LinkItem, StyleType} from '@catui/types';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import {isNullOrUndefined, isUndefined} from 'util';
import {ConfirmationService} from "primeng/primeng";
import {Store} from "@ngrx/store";
import {Subscription} from "rxjs";
import {ROLE, UserService} from "@catcore/services/user.service";
import {DomSanitizer} from "@angular/platform-browser";
import {sortByLabelFunc} from "@catcore/utils";

@Component({
  styleUrls: ['./feature-properties.component.scss'],
  templateUrl: './feature-properties.component.html',
})
export class FeaturePropertiesComponent implements OnInit, OnDestroy {

  @Output() deleteResource = new EventEmitter();

  feature = null;
  featureStatus = null;
  form: FormGroup;

  units: LinkItem[] = [];
  ggwDevices: LinkItem[] = [];
  evidFunctions: LinkItem[] = [];

  panelDisplayType = DisplayTypes;
  styleType = StyleType;

  ROLES = ROLE;

  private featureSubscription: Subscription;
  private subresourceSubscriptions: Subscription[] = [];

  constructor(private route: ActivatedRoute,
              private featureService: FeatureTypeService,
              private featureGgwImplService: FeatureGgwImplementationsService,
              private featureUnitService: FeatureUnitsService,
              private resourceService: GenericResourceApiService,
              private confirmationService: ConfirmationService,
              private router: Router,
              private notification: NotificationsService,
              private userService: UserService) {
  }

  /**
   * Continuously react to a newly provided Feature model and process depending operations.
   * Depending operations are:
   * - creating the properties form
   * - refresh the currently assigned Units
   * - refresh the currently assigned GgwImplementations
   * - refresh the currently assigned EvidFunctions
   */
  ngOnInit() {
    this.featureSubscription = this
      .featureService
      .getCurrent()
      .subscribe((feature: any) => {
        if (feature) {
          this.feature = feature;
          this.featureStatus = this.feature._embedded.status;

          this.form = new FormGroup({
            name: new FormControl(this.feature.name, [Validators.required]),
            description: new FormControl(this.feature.description, [Validators.required]),
            isPublic: new FormControl(this.feature.public_flag, []),
          });

          this.subresourceSubscriptions.forEach((s) => s.unsubscribe());
          this.subresourceSubscriptions = [];

          this.refreshFeatureUnits(feature);
          this.refreshGgwDevices(feature);
          this.refreshEvidFunctions(feature);

        }

      });
  }

  ngOnDestroy() {
    this.featureSubscription.unsubscribe();
    this.subresourceSubscriptions.forEach((s) => s.unsubscribe());
  }

  onSubmit(formData) {

    // this.feature.name = this.form.value.name;
    this.featureService.update({
      id: this.feature.id,
      name: this.form.value.name,
      description: this.form.value.description,
      public_flag: this.form.value.isPublic,
    }).subscribe((result) => {
      this.notification.success(
        'Update successful',
        `The update for Feature '${this.feature.name}' was successful`,
      );
    });
  }

  /**
   * Callback to execute the navigation process for a manually triggered LinkItem
   * @param {LinkItem} item
   */
  navigateTo(item: LinkItem) {
    item.navigate(this.router);
  }

  /**
   * Returns true if the provided Feature model has linked Service information
   * @param {Feature} feature
   * @returns {any}
   */
  hasServices(feature: Feature) {
    const key = 'service_types';
    return Object.keys(feature._links).includes(key);
  }

  /**
   * Returns the linked Service information for a Feature or an empty list if no information can be found
   * @param {Feature} feature
   * @returns {any[]}
   */
  getServices(feature: Feature) {
    const key = 'service_types';
    return Object.keys(feature._links).includes(key) ? feature._links[key] : [];
  }

  /**
   * Constructing a label for a provided FeatureUnit and it's index in a List
   * @param unit
   * @param index
   * @returns {string}
   */
  getLabelForUnit(unit: FeatureUnit, index) {
    let label = `Unit ${index + 1}`;

    if (
        !isNullOrUndefined(unit.display_label) &&
        unit.display_label.length > 0
    ) {
        label = unit.display_label;
    } else if (!isNullOrUndefined(unit.name)) {
      label = unit.name;
    }

    return label;
  }

  /**
   * Fetching multiple resources with a forked join and returns a Observable
   *
   * @param {Array<{href: string}>} resources
   * @returns {Observable<any[]>}
   */
  private retrieveResourcesFor(resources: Array<{ href: string }>) {
    const callJoins = [];

    resources.forEach((resource) => callJoins.push(
      this.resourceService
        .getServiceForResource(resource.href)
        .get()),
    );

    this.subresourceSubscriptions = [...this.subresourceSubscriptions, ...callJoins];
    return Observable.forkJoin(callJoins);
  }

  /**
   * Create a list of LinkItem for FeatureUnit information, extracted from the current Feature.
   *
   * The list contains also the linked Services as a sub-list for every FeatureUnit.
   *
   * @param {Feature} feature
   */
  private refreshFeatureUnits(feature: Feature) {
    const extractedInfos = this.featureService.extractUnitsFor(feature);
    this.retrieveResourcesFor(extractedInfos)
      .subscribe((resources: any[]) => {
        const linkedItems: LinkItem[] = [];

        resources.forEach((featureUnit: any, index: number) => {
          const linkResource: GenericLinkedResource = {label: null, href: featureUnit._links.self.href};
          const linkedItem = {
            label: `Unit ${index}`,
            navigate: (router) => navigateToFeatureResource(router, feature, linkResource),
            raw: featureUnit,
            children: [],
          };

          const services = this.featureUnitService.extractServicesFor(featureUnit);

          services.forEach((service) => {
            linkedItem.children.push({
              label: service.label,
              navigate: (router) => navigateToFeatureResource(router, feature, service),
            });
          });

          linkedItems.push(linkedItem);
        });

        this.units = linkedItems;
      });
  }

  /**
   * Create a list of LinkItem for FeatureGgwImplementation information, extracted from the current Feature.
   *
   * The list contains also the linked Devices as a sub-list for every FeatureGgwImplementation.
   *
   * @param {Feature} feature
   */
  private refreshGgwDevices(feature: Feature) {
    const extractedInfos = this.featureService.extractGgwDevicesFor(feature);
    const linkedItems: LinkItem[] = [];

    extractedInfos.forEach((item: GenericLinkedResource) => {
      linkedItems.push({
        label: item.label,
        navigate: (router: Router) => navigateToFeatureResource(router, feature, item),
      });
    });
    this.ggwDevices = linkedItems;
    this.ggwDevices.sort(sortByLabelFunc);
  }

  /**
   * Create a list of LinkItem for FeatureEvidFunction information, extracted from the current Feature.
   *
   * @param {Feature} feature
   */
  private refreshEvidFunctions(feature: Feature) {
    const extractedInfos = this.featureService.extractEvidFunctionsFor(feature);
    const linkedItems: LinkItem[] = [];

    extractedInfos.forEach((item: GenericLinkedResource) => {
      linkedItems.push({
        label: item.label,
        navigate: (router: Router) => navigateToFeatureResource(router, feature, item),
      });
    });
    this.evidFunctions = linkedItems;
    this.evidFunctions.sort(sortByLabelFunc);
  }

  deleteModel(event) {
    console.log("DeleteModel: ", event);
    this.confirmationService.confirm({
      message: 'Do you really want to delete this feature?',
      accept: () => {

        const subscription = this.featureService.delete(this.feature)
          .finally(() => subscription.unsubscribe())
          .subscribe(() => {
            this.notification.success('Feature deleted', 'The feature was successfully removed.');
            this.router.navigate(['features']);
          });
      },
    });
  }

  cloneModel(event) {
      console.log("CloneModel: ", event);
      this.confirmationService.confirm({
          message: 'Do you really want to clone this feature?',
          accept: () => {

              const subscription = this.featureService.clone(this.feature)
                  .finally(() => subscription.unsubscribe())
                  .subscribe((result) => {
                      this.notification.success('Feature cloned', 'The feature was successfully cloned.');
                      this.router.navigate(['features', result.id, 'properties']);
                  });
          },
      });
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/features/components/feature-properties/feature-properties.component.ts